import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Upload, Save, ExternalLink, Copy, MapPin, KeyRound, User, Globe, Phone, Calendar, FileText, X, CheckCircle2, Circle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { api, formatApiError, API } from "../lib/api";
import { completenessFromForm } from "../lib/profileCompleteness";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function AgentProfile() {
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    const [form, setForm] = useState({
        name: "", phone: "", calendly_link: "", photo_url: "",
        bio: "", website_url: "", license_states: [], password: "",
    });
    const [zipsRaw, setZipsRaw] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/agent/me");
            setMe(data);
            setForm({
                name: data.name || "",
                phone: data.phone || "",
                calendly_link: data.calendly_link || "",
                photo_url: data.photo_url || "",
                bio: data.bio || "",
                website_url: data.website_url || "",
                license_states: data.license_states || [],
                password: "",
            });
            setZipsRaw((data.zip_codes || []).join(", "));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const toggleState = (s) => {
        setForm((f) => ({
            ...f,
            license_states: f.license_states.includes(s)
                ? f.license_states.filter((x) => x !== s)
                : [...f.license_states, s],
        }));
    };

    const onPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingPhoto(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/agent/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            setForm((f) => ({ ...f, photo_url: fullUrl }));
            toast.success("Photo uploaded");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploadingPhoto(false); e.target.value = ""; }
    };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        setSaving(true);
        try {
            const zip_codes = zipsRaw
                .split(/[\s,;\n]+/)
                .map((z) => z.trim())
                .filter(Boolean);
            const payload = {
                name: form.name.trim(),
                phone: form.phone || null,
                calendly_link: form.calendly_link || null,
                photo_url: form.photo_url || null,
                bio: form.bio || null,
                website_url: form.website_url || null,
                license_states: form.license_states,
                zip_codes,
            };
            if (form.password) payload.password = form.password;
            const { data } = await api.patch("/agent/me", payload);
            setMe(data);
            setForm((f) => ({ ...f, password: "" }));
            toast.success("Profile saved");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const publicUrl = me?.slug ? `${window.location.origin}/agent/${me.slug}` : "";
    const completeness = useMemo(() => completenessFromForm(form, zipsRaw), [form, zipsRaw]);
    const copyLink = () => {
        if (!publicUrl) return;
        navigator.clipboard.writeText(publicUrl);
        toast.success("Public link copied");
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-700">Loading…</div>;
    }

    return (
        <div className="min-h-screen bg-slate-50" data-testid="agent-profile-page">
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate("/agent")} data-testid="profile-back-btn" className="rounded-md">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Leads
                        </Button>
                        <div className="hidden sm:block h-6 w-px bg-slate-200" />
                        <div>
                            <div className="font-serif text-xl text-navy-900">My Profile</div>
                            <div className="text-xs text-slate-700">Updates publish instantly to your public page</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {me?.slug && (
                            <>
                                <Button variant="outline" size="sm" onClick={copyLink} data-testid="profile-copy-link" className="rounded-md">
                                    <Copy className="w-4 h-4 mr-2" /> Copy public link
                                </Button>
                                <a href={`/agent/${me.slug}`} target="_blank" rel="noreferrer" data-testid="profile-view-public">
                                    <Button variant="outline" size="sm" className="rounded-md">
                                        <ExternalLink className="w-4 h-4 mr-2" /> View public page
                                    </Button>
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-6">
                {/* Left rail – preview card + completeness */}
                <aside className="lg:col-span-1 space-y-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-6">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-700 mb-4">Public preview</div>
                        <div className="flex flex-col items-center text-center">
                            {form.photo_url ? (
                                <img src={form.photo_url} alt="" className="w-28 h-28 rounded-full object-cover object-top border-4 border-white shadow-md ring-1 ring-slate-200" />
                            ) : (
                                <div className="w-28 h-28 rounded-full bg-navy-900 text-white flex items-center justify-center text-3xl font-serif">{(form.name || "?").slice(0, 1).toUpperCase()}</div>
                            )}
                            <div className="mt-4 font-serif text-xl text-navy-900">{form.name || "Your name"}</div>
                            {me?.email && <div className="text-xs text-slate-700 mt-1">{me.email}</div>}
                            {form.phone && <div className="text-sm text-slate-700 mt-2">{form.phone}</div>}
                            <div className="mt-3 flex flex-wrap justify-center gap-1">
                                {(form.license_states || []).slice(0, 8).map((s) => (
                                    <Badge key={s} variant="secondary" className="text-[11px]">{s}</Badge>
                                ))}
                                {form.license_states.length > 8 && <Badge variant="secondary" className="text-[11px]">+{form.license_states.length - 8}</Badge>}
                            </div>
                            {form.bio && <p className="mt-4 text-sm text-slate-600 leading-relaxed line-clamp-5 text-left">{form.bio}</p>}
                        </div>
                        {publicUrl && (
                            <div className="mt-5 pt-5 border-t border-slate-100 text-xs text-slate-700 break-all">
                                <span className="block uppercase tracking-[0.2em] text-[10px] font-semibold mb-1">Public URL</span>
                                {publicUrl}
                            </div>
                        )}
                    </div>

                    <CompletenessCard completeness={completeness} />
                </aside>

                {/* Right – form */}
                <main className="lg:col-span-2 space-y-6">
                    {/* Photo */}
                    <Section title="Profile photo" icon={Upload}>
                        <div className="flex items-center gap-5">
                            {form.photo_url ? (
                                <img src={form.photo_url} alt="" className="w-24 h-24 rounded-full object-cover object-top border border-slate-200" />
                            ) : (
                                <div className="w-24 h-24 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
                                    <Upload className="w-7 h-7" />
                                </div>
                            )}
                            <div className="flex-1">
                                <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-slate-200 rounded-md text-sm font-medium hover:bg-slate-50">
                                    <Upload className="w-4 h-4 mr-2" /> {uploadingPhoto ? "Uploading…" : (form.photo_url ? "Replace photo" : "Upload photo")}
                                    <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-photo-input" />
                                </label>
                                {form.photo_url && (
                                    <Button variant="ghost" size="sm" className="ml-2" onClick={() => setForm({ ...form, photo_url: "" })} data-testid="profile-photo-clear">
                                        <X className="w-4 h-4 mr-1" /> Remove
                                    </Button>
                                )}
                                <div className="mt-2 text-xs text-slate-700">JPG/PNG, max 5MB. Square works best.</div>
                            </div>
                        </div>
                    </Section>

                    {/* Identity */}
                    <Section title="Identity & contact" icon={User}>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Full name *">
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
                            </Field>
                            <Field label="Email (login)">
                                <Input value={me?.email || ""} disabled className="bg-slate-50 text-slate-700" data-testid="profile-email-readonly" />
                                <div className="mt-1 text-xs text-slate-700">Contact your admin to change your email.</div>
                            </Field>
                            <Field label="Phone">
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 555-5555" data-testid="profile-phone-input" />
                            </Field>
                            <Field label="Calendly link">
                                <Input value={form.calendly_link} onChange={(e) => setForm({ ...form, calendly_link: e.target.value })} placeholder="https://calendly.com/..." data-testid="profile-calendly-input" />
                            </Field>
                            <Field label="Company website" className="sm:col-span-2">
                                <Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://yourcompany.com" data-testid="profile-website-input" />
                            </Field>
                        </div>
                    </Section>

                    {/* Bio */}
                    <Section title="Public bio" icon={FileText}>
                        <Textarea rows={5} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                            placeholder="Short bio shown on your public landing page (specialties, years of experience, philosophy)…"
                            data-testid="profile-bio-input" />
                        <div className="mt-1 text-xs text-slate-700">{form.bio.length} characters</div>
                    </Section>

                    {/* Territory */}
                    <Section title="Territory & licensing" icon={MapPin}>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm text-slate-700">Licensed states</Label>
                                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="profile-states-grid">
                                    {STATES.map((s) => {
                                        const active = form.license_states.includes(s);
                                        return (
                                            <button
                                                type="button"
                                                key={s}
                                                onClick={() => toggleState(s)}
                                                data-testid={`profile-state-${s}`}
                                                className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                                                    active
                                                        ? "bg-navy-900 text-white border-navy-900"
                                                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                                                }`}
                                            >
                                                {s}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="mt-2 text-xs text-slate-700">
                                    {form.license_states.length === 0
                                        ? "No states selected — leads matched only by ZIP."
                                        : `${form.license_states.length} state${form.license_states.length === 1 ? "" : "s"} selected.`}
                                </div>
                            </div>
                            <div>
                                <Label className="text-sm text-slate-700">ZIP codes (priority routing)</Label>
                                <Textarea
                                    rows={3}
                                    value={zipsRaw}
                                    onChange={(e) => setZipsRaw(e.target.value)}
                                    placeholder="74101, 74102, 73101…"
                                    className="mt-1.5 font-mono text-sm"
                                    data-testid="profile-zips-input"
                                />
                                <div className="mt-1 text-xs text-slate-700">Comma, space, or newline separated. Leads from these ZIPs route to you first.</div>
                            </div>
                        </div>
                    </Section>

                    {/* Password */}
                    <Section title="Change password" icon={KeyRound}>
                        <Input
                            type="password"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            placeholder="Leave blank to keep current password"
                            data-testid="profile-password-input"
                        />
                        <div className="mt-1 text-xs text-slate-700">Minimum 8 characters recommended.</div>
                    </Section>

                    <div className="flex items-center justify-end gap-3 pb-12">
                        <Button variant="outline" onClick={() => navigate("/agent")} data-testid="profile-cancel-btn">Cancel</Button>
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="bg-navy-900 hover:bg-navy-800 text-white"
                            data-testid="profile-save-btn"
                        >
                            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </main>
            </div>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
                {Icon && <Icon className="w-4 h-4 text-slate-700" />}
                <h2 className="font-serif text-lg text-navy-900">{title}</h2>
            </div>
            {children}
        </section>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <div className={className}>
            <Label className="text-sm text-slate-700">{label}</Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}

function CompletenessCard({ completeness }) {
    const { percent, completed, missing } = completeness;
    const tone = percent === 100
        ? "text-emerald-700 bg-emerald-50 border-emerald-200"
        : percent >= 60
            ? "text-amber-700 bg-amber-50 border-amber-200"
            : "text-rose-700 bg-rose-50 border-rose-200";
    const barTone = percent === 100 ? "bg-emerald-500" : percent >= 60 ? "bg-amber-500" : "bg-rose-500";

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="profile-completeness-card">
            <div className="flex items-center justify-between mb-4">
                <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-700">Profile completeness</div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${tone}`} data-testid="profile-completeness-percent">{percent}%</span>
            </div>
            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${barTone} transition-all`} style={{ width: `${percent}%` }} />
            </div>
            <div className="mt-4 text-xs text-slate-700">
                {percent === 100
                    ? "Nice — your public page is fully built out."
                    : `${completed.length} of ${completed.length + missing.length} done · agents with complete profiles convert noticeably more leads.`}
            </div>
            {missing.length > 0 && (
                <ul className="mt-4 space-y-1.5" data-testid="profile-completeness-missing">
                    {missing.map((m) => (
                        <li key={m.key} className="flex items-center text-sm text-slate-700">
                            <Circle className="w-3.5 h-3.5 mr-2 text-slate-600" /> {m.label}
                        </li>
                    ))}
                </ul>
            )}
            {completed.length > 0 && missing.length > 0 && (
                <details className="mt-3">
                    <summary className="text-xs text-slate-700 cursor-pointer hover:text-slate-700">Show completed ({completed.length})</summary>
                    <ul className="mt-2 space-y-1.5">
                        {completed.map((c) => (
                            <li key={c.key} className="flex items-center text-sm text-emerald-700">
                                <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> {c.label}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}
